import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useStable } from "../context/StableContext";
import { resolveImage } from "../lib/api";
import {
  Menu, LogOut, ShieldCheck, User, MessageSquarePlus, Megaphone,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const ROLE_LABEL = { admin: "Gestore", manager: "Gestore", staff: "Staff", owner: "Proprietario cavallo", readonly: "Lettura" };

function ContextBlock({ current, headerTitle, idSuffix = "" }) {
  const logo = current?.logo ? resolveImage(current.logo) : null;
  return (
    <div className="min-w-0 flex-1 flex items-center gap-2" data-testid={`topbar-context${idSuffix}`}>
      {logo && <img src={logo} alt="" data-testid={`topbar-stable-logo${idSuffix}`}
        className="h-7 w-7 rounded-md object-contain bg-white border border-[#E5DEC9] shrink-0" />}
      <div className="min-w-0">
        <span data-testid={`topbar-stable-name${idSuffix}`} className="block text-[11px] leading-tight font-medium text-[#8C532B] truncate">
          {current?.name}
        </span>
        {headerTitle && (
          <span data-testid={`topbar-page-title${idSuffix}`} className="block font-display font-bold text-lg leading-tight text-[#1B3B2B] truncate">
            {headerTitle}
          </span>
        )}
      </div>
    </div>
  );
}

function Badges({ user, current }) {
  return (
    <>
      {user?.platform_admin && (
        <span data-testid="platform-admin-badge"
          className="hidden sm:inline-flex font-mono-label text-[10px] rounded-full px-2.5 py-1 bg-[#1B3B2B] text-[#FAF8F5]">
          Platform Admin
        </span>
      )}
      <span data-testid="role-badge"
        className="hidden sm:inline-flex font-mono-label text-[10px] rounded-full px-2.5 py-1 bg-[#F0EAE1] text-[#8C532B] border border-[#E5DEC9]">
        {ROLE_LABEL[current?.role]}
      </span>
    </>
  );
}

function AvatarMenu({ onOpenFeedback, onOpenNews, testId = "user-menu" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initial = (user?.name || "U").slice(0, 1).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={testId} aria-label="Menu account"
          className="h-9 w-9 rounded-full bg-[#8C532B] text-white flex items-center justify-center font-semibold text-sm">
          {initial}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="font-semibold text-sm">{user?.name}</p>
          <p className="text-xs text-[#5F6B64] truncate">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="open-account" onClick={() => navigate("/account")}>
          <User className="h-4 w-4 mr-2" /> Account
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="open-feedback" onClick={onOpenFeedback}>
          <MessageSquarePlus className="h-4 w-4 mr-2" /> Feedback
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="open-news" onClick={onOpenNews}>
          <Megaphone className="h-4 w-4 mr-2" /> Novità
        </DropdownMenuItem>
        {user?.platform_admin && (
          <DropdownMenuItem data-testid="open-platform-admin" onClick={() => navigate("/platform-admin")}>
            <ShieldCheck className="h-4 w-4 mr-2" /> Console Platform Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600">
          <LogOut className="h-4 w-4 mr-2" /> Esci
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Unified full-width top bar.
//  - Equiro logo = global shell element, fixed at the left (independent of sidebar state).
//  - Context (stable + page title) + avatar = content-aligned: on desktop they mirror the exact
//    same shell layout as the page (sidebar spacer + max-w-7xl mx-auto container as <main>),
//    so the context starts on the same vertical as the main content across widths and sidebar modes.
export default function TopBar({ onOpenDrawer, onOpenFeedback, onOpenNews, headerTitle, sidebarMode }) {
  const { user } = useAuth();
  const { current } = useStable();
  const navigate = useNavigate();
  const spacerWidth = sidebarMode === "open" ? "w-60" : "w-[68px]";

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF8F5]/90 border-b border-[#E5DEC9] pt-safe px-safe"
    >
      {/* MOBILE row (unchanged) */}
      <div className="lg:hidden px-4 sm:px-6 h-16 flex items-center gap-3">
        <button data-testid="mobile-menu-btn" onClick={onOpenDrawer}
          className="-ml-1 p-2 text-[#1B3B2B]" aria-label="Apri menu di navigazione">
          <Menu className="h-6 w-6" />
        </button>
        <button data-testid="logo-home" onClick={() => navigate("/")}
          className="flex items-center gap-2 mr-1 shrink-0 cursor-pointer" aria-label="Vai alla Dashboard">
          <img src="/icon-192.png" alt="Equiro" className="h-9 w-9 rounded-xl object-cover" />
        </button>
        <ContextBlock current={current} headerTitle={headerTitle} />
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <Badges user={user} current={current} />
          <div data-testid="notification-slot" className="flex items-center" aria-hidden="true" />
          <AvatarMenu onOpenFeedback={onOpenFeedback} onOpenNews={onOpenNews} />
        </div>
      </div>

      {/* DESKTOP row: logo fixed at the left (shell); context+avatar aligned to the page content */}
      <div className="hidden lg:block relative h-16">
        <button data-testid="logo-home-desktop" onClick={() => navigate("/")}
          className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-10 flex items-center gap-2 shrink-0 cursor-pointer"
          aria-label="Vai alla Dashboard">
          <img src="/icon-192.png" alt="Equiro" className="h-9 w-9 rounded-xl object-cover" />
          <span className="font-display font-extrabold text-xl text-[#1B3B2B] tracking-tight">Equiro</span>
        </button>

        <div className="flex h-full">
          {/* Mirrors AppLayout sidebar spacer (open ~240px, closed/hover ~68px) */}
          <div aria-hidden className={`shrink-0 transition-[width] duration-200 ${spacerWidth}`} />
          <div className="flex-1 min-w-0">
            {/* Mirrors <main>: same max-width + horizontal padding => identical left/right edges */}
            <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 h-full flex items-center gap-3">
              <ContextBlock current={current} headerTitle={headerTitle} idSuffix="-desktop" />
              <div className="ml-auto flex items-center gap-3">
                <Badges user={user} current={current} />
                <div data-testid="notification-slot-desktop" className="flex items-center" aria-hidden="true" />
                <AvatarMenu onOpenFeedback={onOpenFeedback} onOpenNews={onOpenNews} testId="user-menu-desktop" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
